/**
 * Voice Settings tab — availability toggle + caller ID management + agent config.
 * Part of the Call Manager modal.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Phone,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Link2,
  Unlink,
  Loader2,
  Users,
  X,
  Check,
  MessageCircle,
  Copy,
  Bell,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useCallStore, isValidAudioUrl } from '@/stores/call-store';
import { useAppUsersStore } from '@/stores/app-users-store';
import { useAuthStore, getEffectiveUid } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { useModuleStore } from '@/stores/module-store';
import { useValidAgentEmails, filterValidAgents, isSameAgentEmail } from './use-valid-agent-emails';

export function VoiceSettings() {
  const isAvailable = useCallStore((s) => s.isAvailable);
  const toggleAvailability = useCallStore((s) => s.toggleAvailability);
  const availabilityUpdating = useCallStore((s) => s.availabilityUpdating);
  const allVoiceNumbers = useCallStore((s) => s.allVoiceNumbers);
  const linkingNumberSid = useCallStore((s) => s.linkingNumberSid);
  const linkNumber = useCallStore((s) => s.linkNumber);
  const unlinkNumber = useCallStore((s) => s.unlinkNumber);
  const whatsAppVoiceNumbers = useCallStore((s) => s.whatsAppVoiceNumbers);
  const linkingWhatsAppSenderSid = useCallStore((s) => s.linkingWhatsAppSenderSid);
  const linkWhatsAppSender = useCallStore((s) => s.linkWhatsAppSender);
  const unlinkWhatsAppSender = useCallStore((s) => s.unlinkWhatsAppSender);
  const incomingCallAgents = useCallStore((s) => s.incomingCallAgents);
  const saveIncomingCallAgents = useCallStore((s) => s.saveIncomingCallAgents);
  const twiMLAppId = useCallStore((s) => s.twiMLAppId);
  const ensureApiReady = useCallStore((s) => s.ensureApiReady);
  const refreshVoiceConfig = useCallStore((s) => s.refreshVoiceConfig);

  const extensionName = useAppStore((s) => s.extensionName);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);

  // Agent config popup state
  const [agentConfigCallerId, setAgentConfigCallerId] = useState<string | null>(null);

  // Bootstrap API + always fetch fresh numbers when settings is opened
  useEffect(() => {
    if (!tpaAuthenticated || !extensionName) return;
    ensureApiReady(extensionName).then(() => {
      // Clear cache so Settings always fetches fresh from Twilio
      try { localStorage.removeItem('ulgebra_voice_numbers_cache'); } catch { /* */ }
      refreshVoiceConfig();
    });
  }, [tpaAuthenticated, extensionName, ensureApiReady, refreshVoiceConfig]);

  // Check if current user is admin (not a shared user)
  const user = useAuthStore((s) => s.user);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const isAdmin = !selectedAdminUserId || selectedAdminUserId === user?.uid;

  const validAgentEmails = useValidAgentEmails();

  const hasLinkedNumber =
    allVoiceNumbers.some((n) => n.linked) ||
    whatsAppVoiceNumbers.some((n) => n.linked);
  const hasAvailableNumber = allVoiceNumbers.length > 0 || whatsAppVoiceNumbers.length > 0;

  return (
    <div className="px-4 py-3 space-y-5">
      {/* Alert when no numbers are linked for incoming */}
      {!hasLinkedNumber && hasAvailableNumber && (
        <div className="flex items-start gap-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 p-3">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Incoming calls not enabled
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
              Link at least one phone number below to start receiving incoming calls through this app.
            </p>
          </div>
        </div>
      )}

      {/* Availability toggle */}
      {(() => {
        const effectiveAvailable = isAvailable && hasLinkedNumber;
        return (
          <div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Available for Calls
                </p>
                <p className="text-xs text-muted-foreground">
                  {!hasLinkedNumber
                    ? 'Link a phone number below to enable'
                    : effectiveAvailable
                      ? 'You will receive incoming calls'
                      : 'Incoming calls are disabled'}
                </p>
              </div>
              <button
                onClick={toggleAvailability}
                disabled={!hasLinkedNumber || availabilityUpdating}
                title={!hasLinkedNumber ? 'Link a phone number below to enable availability' : undefined}
                className={cn(
                  'relative h-6 w-11 rounded-full transition-colors duration-200',
                  effectiveAvailable ? 'bg-emerald-500' : 'bg-muted-foreground/30',
                  !hasLinkedNumber && 'opacity-50 cursor-not-allowed',
                  availabilityUpdating && 'opacity-60 cursor-wait'
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200',
                    effectiveAvailable && 'translate-x-5'
                  )}
                />
              </button>
            </div>
          </div>
        );
      })()}

      {/* Voice-capable numbers with link/unlink */}
      <div>
        <h3 className="text-xs font-medium text-muted-foreground mb-2">
          Phone Numbers
        </h3>

        {allVoiceNumbers.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-4 text-center">
            <Phone className="h-6 w-6 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-xs text-muted-foreground">
              No voice-capable numbers found
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Purchase a phone number in your Twilio console
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {allVoiceNumbers.map((num) => {
              const isLinking = linkingNumberSid === num.sid;
              const cleanId = num.phoneNumber.replace(/[^0-9A-Za-z]+/g, '');
              const assignedAgents = filterValidAgents(incomingCallAgents[cleanId] || [], validAgentEmails);
              return (
                <div key={num.sid} className={cn('rounded-lg px-3 py-2', num.linked ? 'bg-emerald-500/5 border border-emerald-500/15' : 'bg-muted/20')}>
                  <div className="flex items-center gap-3">
                    <CheckCircle2
                      className={cn(
                        'h-4 w-4 shrink-0',
                        num.linked ? 'text-emerald-500' : 'text-muted-foreground/30'
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground truncate">
                        {num.phoneNumber}
                      </p>
                      {num.friendlyName !== num.phoneNumber && (
                        <p className="text-xs text-muted-foreground truncate">
                          {num.friendlyName}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() =>
                        num.linked ? unlinkNumber(num.sid) : linkNumber(num.sid)
                      }
                      disabled={isLinking}
                      className={cn(
                        'shrink-0 flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                        num.linked
                          ? 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
                          : 'text-primary bg-primary/10 hover:bg-primary/20',
                        isLinking && 'opacity-50 cursor-not-allowed'
                      )}
                      title={num.linked ? 'Unlink from voice calling' : 'Link for voice calling'}
                    >
                      {isLinking ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : num.linked ? (
                        <Unlink className="h-3 w-3" />
                      ) : (
                        <Link2 className="h-3 w-3" />
                      )}
                      {num.linked ? 'Unlink' : 'Link'}
                    </button>
                  </div>

                  {/* Agent assignment row — only for linked numbers, admin only */}
                  {num.linked && isAdmin && (
                    <div className="mt-1.5 ml-7 flex items-center gap-1.5">
                      <Users className="h-3 w-3 text-muted-foreground shrink-0" />
                      <button
                        onClick={() => setAgentConfigCallerId(num.phoneNumber)}
                        className="text-xs text-primary hover:underline"
                      >
                        {assignedAgents.length > 0
                          ? `${assignedAgents.length} agent${assignedAgents.length > 1 ? 's' : ''} assigned`
                          : 'Configure call agents'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {allVoiceNumbers.length > 0 && (
          <p className="text-xs text-muted-foreground/60 mt-2">
            Linked numbers can be used as caller ID for outbound calls and will receive inbound calls.
          </p>
        )}
      </div>

      {/* WhatsApp Calling section */}
      {whatsAppVoiceNumbers.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
            <MessageCircle className="h-3 w-3" />
            WhatsApp Calling
          </h3>

          <div className="space-y-1.5">
            {whatsAppVoiceNumbers.map((sender) => {
              const isLinking = linkingWhatsAppSenderSid === sender.sid;
              const cleanId = sender.phoneNumber.replace(/[^0-9A-Za-z]+/g, '');
              const assignedAgents = filterValidAgents(incomingCallAgents[cleanId] || [], validAgentEmails);
              // Display number without whatsapp: prefix
              const displayNumber = sender.phoneNumber.replace('whatsapp:', '');
              return (
                <div key={sender.sid} className={cn('rounded-lg px-3 py-2', sender.linked ? 'bg-emerald-500/5 border border-emerald-500/15' : 'bg-muted/20')}>
                  <div className="flex items-center gap-3">
                    <CheckCircle2
                      className={cn(
                        'h-4 w-4 shrink-0',
                        sender.linked ? 'text-emerald-500' : 'text-muted-foreground/30'
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground truncate flex items-center gap-1.5">
                        <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-green-500/15 shrink-0">
                          <MessageCircle className="h-2.5 w-2.5 text-green-600 dark:text-green-400" />
                        </span>
                        {displayNumber}
                      </p>
                      {sender.friendlyName !== sender.phoneNumber && (
                        <p className="text-xs text-muted-foreground truncate ml-5.5">
                          {sender.friendlyName}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() =>
                        sender.linked ? unlinkWhatsAppSender(sender.sid) : linkWhatsAppSender(sender.sid)
                      }
                      disabled={isLinking}
                      className={cn(
                        'shrink-0 flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                        sender.linked
                          ? 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
                          : 'text-primary bg-primary/10 hover:bg-primary/20',
                        isLinking && 'opacity-50 cursor-not-allowed'
                      )}
                      title={sender.linked ? 'Disable voice calling' : 'Enable voice calling'}
                    >
                      {isLinking ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : sender.linked ? (
                        <Unlink className="h-3 w-3" />
                      ) : (
                        <Link2 className="h-3 w-3" />
                      )}
                      {sender.linked ? 'Unlink' : 'Link'}
                    </button>
                  </div>

                  {/* Agent assignment row — only for linked senders, admin only */}
                  {sender.linked && isAdmin && (
                    <div className="mt-1.5 ml-7 flex items-center gap-1.5">
                      <Users className="h-3 w-3 text-muted-foreground shrink-0" />
                      <button
                        onClick={() => setAgentConfigCallerId(sender.phoneNumber)}
                        className="text-xs text-primary hover:underline"
                      >
                        {assignedAgents.length > 0
                          ? `${assignedAgents.length} agent${assignedAgents.length > 1 ? 's' : ''} assigned`
                          : 'Configure call agents'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground/60 mt-2">
            Linked WhatsApp senders can receive and make WhatsApp voice calls.
          </p>
        </div>
      )}



      {/* TwiML App ID */}
      <div className="rounded-md bg-muted/30 px-3 py-3">
        <p className="text-xs text-muted-foreground font-medium mb-1.5">TwiML App SID</p>
        <div className="flex items-center gap-2">
          <code className="text-sm text-foreground font-mono truncate flex-1">{twiMLAppId || 'Not configured'}</code>
          {twiMLAppId && (
            <a
              href={`https://console.twilio.com/us1/develop/voice/manage/twiml-apps/${twiMLAppId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
              title="Open in Twilio Console"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>

      {/* Hold Music — admin only */}
      {isAdmin && <HoldMusicSection />}

      {/* Auto call recording — admin only */}
      {isAdmin && <AutoRecordSection />}

      {/* Missed Call Notifications — admin only */}
      {isAdmin && <MissedCallNotificationsSection />}

      {/* Business Hours — admin only */}
      {isAdmin && <BusinessHoursSection />}

      {/* Ring Groups — admin only */}
      {isAdmin && <RingGroupsSection />}

      {/* Help link */}
      <div className="pt-2 border-t border-border">
        <a
          href="https://www.twilio.com/docs/voice/sdks/javascript/get-started"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-primary hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Twilio Voice SDK Documentation
        </a>
      </div>

      {/* Agent configuration popup */}
      {agentConfigCallerId && (
        <AgentConfigPopup
          callerId={agentConfigCallerId}
          assignedAgents={
            // Raw list on purpose: the popup filters reactively via
            // useValidAgentEmails, so a late org-users load can't freeze a
            // stale filter into the seeded selection.
            incomingCallAgents[agentConfigCallerId.replace(/[^0-9A-Za-z]+/g, '')] || []
          }
          onSave={(agents) => {
            saveIncomingCallAgents(agentConfigCallerId, agents);
            setAgentConfigCallerId(null);
          }}
          onClose={() => setAgentConfigCallerId(null)}
        />
      )}
    </div>
  );
}

// ── Hold Music ────────────────────────────────────────────────────

export function HoldMusicSection() {
  const extensionName = useAppStore((s) => s.extensionName);
  const setHoldMusicUrl = useCallStore((s) => s.setHoldMusicUrl);
  /** Last persisted value — what Cancel restores and view mode displays */
  const [savedUrl, setSavedUrl] = useState('');
  const [musicUrl, setMusicUrl] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // getEffectiveUid, not selectedAdminUserId||uid — must match what
    // _getHoldMusicUrl reads at hold time (differs under impersonation).
    const uid = getEffectiveUid();
    if (!uid || !extensionName) return;

    import('@/config/firebase').then(({ db }) => {
      import('@/lib/firebase/firestore').then(({ doc, getDoc }) => {
        getDoc(doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/callSettings/holdSettings`))
          .then((snap) => {
            const value = snap.exists() ? (snap.data().musicUrl ?? '') : '';
            setSavedUrl(value);
            setMusicUrl(value);
            setLoaded(true);
          })
          .catch(() => setLoaded(true));
      });
    });
  }, [extensionName]);

  const persist = async (value: string) => {
    const uid = getEffectiveUid();
    if (!uid || !extensionName) return;

    setSaving(true);
    try {
      const { db } = await import('@/config/firebase');
      const { doc, setDoc } = await import('@/lib/firebase/firestore');
      await setDoc(doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/callSettings/holdSettings`), {
        musicUrl: value,
      });
      setHoldMusicUrl(value);
      setSavedUrl(value);
      setMusicUrl(value);
      setEditing(false);
      toast.success(value ? 'Saved' : 'Using Twilio default hold music');
    } catch (err) {
      console.error('[HoldMusic] Save failed:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    // A non-URL here is worse than no URL: Twilio can't fetch it as wait
    // music, plays an error announcement to the held caller, and drops them.
    const trimmed = musicUrl.trim();
    if (trimmed && !isValidAudioUrl(trimmed)) {
      toast.error('Enter a full http(s) URL to an audio file, or leave empty for default music');
      return;
    }
    void persist(trimmed);
  };

  const handleCancel = () => {
    setMusicUrl(savedUrl);
    setEditing(false);
  };

  if (!loaded) return null;

  return (
    <div className="px-4 py-3 space-y-3">
      <div>
        <p className="text-sm font-medium text-foreground">Hold Music</p>
        <p className="text-xs text-muted-foreground">
          Played to the other party while a call is on hold
        </p>
      </div>

      {!editing ? (
        /* View mode — current value + Edit */
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
          <p
            className={cn(
              'flex-1 min-w-0 truncate text-sm',
              savedUrl ? 'text-foreground' : 'text-muted-foreground'
            )}
            title={savedUrl || undefined}
          >
            {savedUrl || "Twilio's default hold music"}
          </p>
          <button
            onClick={() => setEditing(true)}
            className="shrink-0 text-xs font-medium text-primary hover:underline"
          >
            Edit
          </button>
          {savedUrl && (
            <button
              onClick={() => void persist('')}
              disabled={saving}
              className="shrink-0 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              Use default music
            </button>
          )}
        </div>
      ) : (
        /* Edit mode — input + Save/Cancel */
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground font-medium">
              Audio file URL (MP3/WAV)
            </label>
            <input
              type="url"
              value={musicUrl}
              onChange={(e) => setMusicUrl(e.target.value)}
              placeholder="https://example.com/hold-music.mp3"
              autoFocus
              className="mt-1.5 w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="text-[10px] text-muted-foreground/70 mt-1">
              Leave empty to use Twilio's default hold music. Short clips repeat for the whole hold.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Auto Call Recording (KAN-180) ─────────────────────────────────

export function AutoRecordSection() {
  const extensionName = useAppStore((s) => s.extensionName);
  const setAutoRecordCalls = useCallStore((s) => s.setAutoRecordCalls);
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // getEffectiveUid — must match what _getAutoRecordSetting reads at call time.
    const uid = getEffectiveUid();
    if (!uid || !extensionName) return;

    import('@/config/firebase').then(({ db }) => {
      import('@/lib/firebase/firestore').then(({ doc, getDoc }) => {
        getDoc(doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/callSettings/recordingSettings`))
          .then((snap) => {
            setEnabled(snap.exists() && snap.data().autoRecord === true);
            setLoaded(true);
          })
          .catch(() => setLoaded(true));
      });
    });
  }, [extensionName]);

  const handleToggle = async () => {
    const uid = getEffectiveUid();
    if (!uid || !extensionName || saving) return;

    const next = !enabled;
    setSaving(true);
    try {
      const { db } = await import('@/config/firebase');
      const { doc, setDoc } = await import('@/lib/firebase/firestore');
      await setDoc(
        doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/callSettings/recordingSettings`),
        { autoRecord: next },
        { merge: true }
      );
      setEnabled(next);
      setAutoRecordCalls(next);
      toast.success(next ? 'Every call will be recorded' : 'Auto-recording turned off');
    } catch (err) {
      console.error('[AutoRecord] Save failed:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">Auto-record calls</p>
          <p className="text-xs text-muted-foreground">
            Start recording every call as soon as it is answered. Agents can still pause it.
          </p>
        </div>
        <button
          onClick={() => void handleToggle()}
          disabled={saving}
          role="switch"
          aria-checked={enabled}
          data-testid="auto-record-toggle"
          className={cn(
            'relative h-6 w-11 shrink-0 rounded-full transition-colors duration-200 disabled:opacity-50',
            enabled ? 'bg-emerald-500' : 'bg-muted-foreground/30'
          )}
        >
          <span className={cn(
            'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200',
            enabled && 'translate-x-5'
          )} />
        </button>
      </div>
    </div>
  );
}

// ── Missed Call Notifications ─────────────────────────────────────

export function MissedCallNotificationsSection() {
  const extensionName = useAppStore((s) => s.extensionName);
  const [enabled, setEnabled] = useState(false);
  const [emailList, setEmailList] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const uid = useAuthStore.getState().selectedAdminUserId || useAuthStore.getState().user?.uid;
    if (!uid || !extensionName) return;

    import('@/config/firebase').then(({ db }) => {
      import('@/lib/firebase/firestore').then(({ doc, getDoc }) => {
        getDoc(doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/callSettings/missedCallNotifications`))
          .then((snap) => {
            if (snap.exists()) {
              const data = snap.data();
              setEnabled(data.enabled ?? false);
              setEmailList((data.emails ?? []).join(', '));
            }
            setLoaded(true);
          })
          .catch(() => setLoaded(true));
      });
    });
  }, [extensionName]);

  const handleSave = async () => {
    const uid = useAuthStore.getState().selectedAdminUserId || useAuthStore.getState().user?.uid;
    if (!uid || !extensionName) return;

    setSaving(true);
    try {
      const { db } = await import('@/config/firebase');
      const { doc, setDoc } = await import('@/lib/firebase/firestore');
      const emails = emailList.split(',').map((e) => e.trim()).filter(Boolean);
      await setDoc(doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/callSettings/missedCallNotifications`), {
        enabled,
        emails,
      });
      toast.success('Saved');
    } catch (err) {
      console.error('[MissedCallNotifications] Save failed:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="px-4 py-3 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">Email Alerts</p>
          <p className="text-xs text-muted-foreground">Get notified when a call is missed</p>
        </div>
        <button
          onClick={() => setEnabled(!enabled)}
          className={cn(
            'relative h-6 w-11 rounded-full transition-colors duration-200',
            enabled ? 'bg-emerald-500' : 'bg-muted-foreground/30'
          )}
        >
          <span className={cn(
            'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200',
            enabled && 'translate-x-5'
          )} />
        </button>
      </div>

      {enabled && (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground font-medium">
              Email addresses (comma-separated)
            </label>
            <input
              type="text"
              value={emailList}
              onChange={(e) => setEmailList(e.target.value)}
              placeholder="admin@company.com, support@company.com"
              className="mt-1.5 w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Business Hours Config ────────────────────────────────────────

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function BusinessHoursSection() {
  const extensionName = useAppStore((s) => s.extensionName);
  const [enabled, setEnabled] = useState(false);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [schedules, setSchedules] = useState<Array<{ days: number[]; startTime: string; endTime: string }>>([
    { days: [1, 2, 3, 4, 5], startTime: '09:00', endTime: '17:00' },
  ]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Load from Firestore
  useEffect(() => {
    const uid = useAuthStore.getState().selectedAdminUserId || useAuthStore.getState().user?.uid;
    if (!uid || !extensionName) return;

    import('@/config/firebase').then(({ db }) => {
      import('@/lib/firebase/firestore').then(({ doc, getDoc }) => {
        getDoc(doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/callSettings/businessHours`))
          .then((snap) => {
            if (snap.exists()) {
              const data = snap.data();
              setEnabled(data.enabled ?? false);
              setTimezone(data.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
              if (data.schedules?.length) setSchedules(data.schedules);
            }
            setLoaded(true);
          })
          .catch(() => setLoaded(true));
      });
    });
  }, [extensionName]);

  const handleSave = async () => {
    const uid = useAuthStore.getState().selectedAdminUserId || useAuthStore.getState().user?.uid;
    if (!uid || !extensionName) return;

    setSaving(true);
    try {
      const { db } = await import('@/config/firebase');
      const { doc, setDoc } = await import('@/lib/firebase/firestore');
      await setDoc(doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/callSettings/businessHours`), {
        enabled,
        timezone,
        schedules,
      });
    } catch (err) {
      console.error('[BusinessHours] Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (scheduleIdx: number, day: number) => {
    setSchedules((prev) =>
      prev.map((s, i) => {
        if (i !== scheduleIdx) return s;
        const days = s.days.includes(day) ? s.days.filter((d) => d !== day) : [...s.days, day].sort();
        return { ...s, days };
      })
    );
  };

  const updateSchedule = (idx: number, field: 'startTime' | 'endTime', value: string) => {
    setSchedules((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const addSchedule = () => {
    setSchedules((prev) => [...prev, { days: [], startTime: '09:00', endTime: '17:00' }]);
  };

  const removeSchedule = (idx: number) => {
    setSchedules((prev) => prev.filter((_, i) => i !== idx));
  };

  if (!loaded) return null;

  return (
    <div className="px-4 py-3 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">Business Hours Routing</p>
          <p className="text-xs text-muted-foreground">Route calls differently outside business hours</p>
        </div>
        <button
          onClick={() => setEnabled(!enabled)}
          className={cn(
            'relative h-6 w-11 rounded-full transition-colors duration-200',
            enabled ? 'bg-emerald-500' : 'bg-muted-foreground/30'
          )}
        >
          <span className={cn(
            'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200',
            enabled && 'translate-x-5'
          )} />
        </button>
      </div>

      {enabled && (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground font-medium">Timezone</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {Intl.supportedValuesOf('timeZone').map((tz) => (
                <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>

          {schedules.map((schedule, idx) => (
            <div key={idx} className="rounded-lg border border-border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Schedule {idx + 1}</span>
                {schedules.length > 1 && (
                  <button
                    onClick={() => removeSchedule(idx)}
                    className="text-xs text-muted-foreground hover:text-destructive"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="flex gap-1.5">
                {DAYS.map((label, dayIdx) => (
                  <button
                    key={dayIdx}
                    onClick={() => toggleDay(idx, dayIdx)}
                    className={cn(
                      'h-8 w-8 rounded-full text-xs font-medium transition-colors',
                      schedule.days.includes(dayIdx)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                    )}
                  >
                    {label[0]}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="time"
                  value={schedule.startTime}
                  onChange={(e) => updateSchedule(idx, 'startTime', e.target.value)}
                  className="flex-1 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <input
                  type="time"
                  value={schedule.endTime}
                  onChange={(e) => updateSchedule(idx, 'endTime', e.target.value)}
                  className="flex-1 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between">
            <button
              onClick={addSchedule}
              className="text-sm text-primary hover:underline"
            >
              + Add Schedule
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ring Groups Section ───────────────────────────────────────────

export function RingGroupsSection() {
  const ringGroups = useCallStore((s) => s.ringGroups);
  const ringGroupsLoading = useCallStore((s) => s.ringGroupsLoading);
  const fetchRingGroups = useCallStore((s) => s.fetchRingGroups);
  const saveRingGroup = useCallStore((s) => s.saveRingGroup);
  const deleteRingGroup = useCallStore((s) => s.deleteRingGroup);

  const [editingGroup, setEditingGroup] = useState<{
    id: string;
    name: string;
    agents: string[];
    ringStrategy: 'simultaneous' | 'sequential';
    ringTimeout: number;
  } | null>(null);

  const orgUsers = useAppUsersStore((s) => s.orgUsers);
  const loadOrgUsers = useAppUsersStore((s) => s.loadOrgUsers);
  const validAgentEmails = useValidAgentEmails();

  useEffect(() => {
    fetchRingGroups();
  }, [fetchRingGroups]);

  // Saving a group prunes emails missing from the org list (same staleness
  // rules as the Call Agents popup), so refresh from the server whenever an
  // editor opens. Keyed on the group id — editingGroup changes per keystroke.
  const editingGroupId = editingGroup?.id;
  useEffect(() => {
    if (editingGroupId) loadOrgUsers({ force: true });
  }, [editingGroupId, loadOrgUsers]);

  const userList = useMemo(() => Object.values(orgUsers).map((u) => ({ email: u.email, name: u.name })), [orgUsers]);

  // Ring order matters for sequential groups — filter preserves it.
  const effectiveGroupAgents = useMemo(
    () => (editingGroup ? filterValidAgents(editingGroup.agents, validAgentEmails) : []),
    [editingGroup, validAgentEmails]
  );

  /** The group's agents as stored, so a retick can restore the exact spelling. */
  const originalGroupAgents = useRef<string[]>([]);

  const handleNew = () => {
    originalGroupAgents.current = [];
    setEditingGroup({
      id: `rg_${Date.now().toString(36)}`,
      name: '',
      agents: [],
      ringStrategy: 'simultaneous',
      ringTimeout: 30,
    });
  };

  const handleSave = async () => {
    if (!editingGroup || !editingGroup.name.trim() || effectiveGroupAgents.length === 0) return;
    // saveRingGroup toasts on failure; keep the editor open so the edit
    // isn't silently discarded.
    const saved = await saveRingGroup({ ...editingGroup, agents: effectiveGroupAgents });
    if (saved) setEditingGroup(null);
  };

  const handleEdit = (group: typeof ringGroups[0]) => {
    originalGroupAgents.current = group.agents;
    setEditingGroup({ ...group });
  };

  // Compare case-insensitively, like filterValidAgents: a saved email whose
  // casing differs from the org record must read as selected, or ticking the
  // row would store the same person twice.
  const isAgentPicked = (email: string) =>
    !!editingGroup && editingGroup.agents.some((a) => isSameAgentEmail(a, email));

  const toggleAgent = (email: string) => {
    if (!editingGroup) return;
    const agents = isAgentPicked(email)
      ? editingGroup.agents.filter((a) => !isSameAgentEmail(a, email))
      : // Re-adding restores the ORIGINAL stored spelling when one existed —
        // Twilio client identities are case-sensitive, so an untick+retick
        // must not silently rewrite the string the device registered under.
        // Pick the LAST case-variant so the retick keeps the same spelling
        // filterValidAgents does (last wins) — else Save diverges by path.
        [...editingGroup.agents, [...originalGroupAgents.current].reverse().find((a) => isSameAgentEmail(a, email)) ?? email];
    setEditingGroup({ ...editingGroup, agents });
  };

  return (
    <div className="px-4 py-3 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">Ring Groups</p>
          <p className="text-xs text-muted-foreground">Organize agents into groups for call routing</p>
        </div>
        <button
          onClick={handleNew}
          className="text-sm text-primary hover:underline"
        >
          + Add Group
        </button>
      </div>

      {ringGroupsLoading && ringGroups.length === 0 && (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
        </div>
      )}

      {ringGroups.length === 0 && !ringGroupsLoading && !editingGroup && (
        <div className="rounded-lg border border-dashed border-border p-6 text-center">
          <Users className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">No ring groups configured</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Create groups to organize agents for call routing
          </p>
        </div>
      )}

      {/* Existing groups */}
      <div className="space-y-2">
        {ringGroups.map((group) => (
          <div key={group.id} className="rounded-lg border border-border px-4 py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{group.name}</p>
              <p className="text-xs text-muted-foreground">
                {(() => {
                  const n = filterValidAgents(group.agents, validAgentEmails).length;
                  return `${n} agent${n !== 1 ? 's' : ''}`;
                })()} · {group.ringStrategy === 'simultaneous' ? 'Ring All' : 'Sequential'}
              </p>
            </div>
            <button
              onClick={() => handleEdit(group)}
              className="text-xs text-primary hover:underline shrink-0"
            >
              Edit
            </button>
            <button
              onClick={() => deleteRingGroup(group.id)}
              className="text-xs text-muted-foreground hover:text-destructive shrink-0"
            >
              Delete
            </button>
          </div>
        ))}
      </div>

      {/* Editing form */}
      {editingGroup && (
        <div className="rounded-lg border border-border p-4 space-y-4">
          <input
            type="text"
            value={editingGroup.name}
            onChange={(e) => setEditingGroup({ ...editingGroup, name: e.target.value })}
            placeholder="Group name (e.g. Sales)"
            className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary"
          />

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">Ring Strategy</p>
            <div className="flex gap-2">
              {(['simultaneous', 'sequential'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setEditingGroup({ ...editingGroup, ringStrategy: s })}
                  className={cn(
                    'rounded-full px-4 py-1.5 text-xs font-medium transition-colors',
                    editingGroup.ringStrategy === s
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  )}
                >
                  {s === 'simultaneous' ? 'Ring All' : 'Sequential'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">Timeout (seconds)</p>
            <input
              type="number"
              value={editingGroup.ringTimeout}
              min={5}
              max={120}
              onChange={(e) => setEditingGroup({ ...editingGroup, ringTimeout: Number(e.target.value) })}
              className="w-24 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Agents ({effectiveGroupAgents.length} selected)
            </p>
            <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg border border-border p-2">
              {userList.map((u) => {
                const picked = isAgentPicked(u.email);
                return (
                  <button
                    key={u.email}
                    onClick={() => toggleAgent(u.email)}
                    className={cn(
                      'w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                      picked
                        ? 'bg-primary/10 text-foreground'
                        : 'hover:bg-muted/50 text-muted-foreground'
                    )}
                  >
                    <div className={cn(
                      'h-4 w-4 rounded border shrink-0 flex items-center justify-center',
                      picked
                        ? 'bg-primary border-primary text-primary-foreground'
                        : 'border-border'
                    )}>
                      {picked && <Check className="h-3 w-3" />}
                    </div>
                    <span className="truncate">{u.name || u.email}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setEditingGroup(null)}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!editingGroup.name.trim() || effectiveGroupAgents.length === 0}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Agent Config Popup ────────────────────────────────────────────

function AgentConfigPopup({
  callerId,
  assignedAgents,
  onSave,
  onClose,
}: {
  callerId: string;
  assignedAgents: string[];
  onSave: (agents: string[]) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(assignedAgents));
  const orgUsers = useAppUsersStore((s) => s.orgUsers);
  const loadOrgUsers = useAppUsersStore((s) => s.loadOrgUsers);
  const currentUserEmail = useAuthStore((s) => s.user?.email);
  const validAgentEmails = useValidAgentEmails();

  // Save prunes emails missing from the org list, so it must not act on a
  // session-stale (or SDK-cached) copy: refresh from the server on open.
  // While the refresh is in flight the valid set is null and Save fail-opens.
  useEffect(() => {
    loadOrgUsers({ force: true });
  }, [loadOrgUsers]);

  const userList = useMemo(() => {
    const users = Object.values(orgUsers).map((u) => ({
      email: u.email,
      name: u.name,
      picture: u.picture,
    }));
    // Ensure current user is always in the list
    if (currentUserEmail && !users.some((u) => u.email === currentUserEmail)) {
      users.unshift({ email: currentUserEmail, name: 'You', picture: undefined });
    }
    return users;
  }, [orgUsers, currentUserEmail]);

  // Saved lists can hold emails of users since removed from the org; those
  // have no row to uncheck, so exclude them from the count and from Save.
  // While the valid set is untrusted (null) the raw selection is kept — never
  // silently drop agents on Save in that state.
  const effectiveSelected = useMemo(
    // filterValidAgents also drops case-variant duplicates, so two spellings
    // of one person count, save, and dial once.
    () => new Set(filterValidAgents(Array.from(selected), validAgentEmails)),
    [selected, validAgentEmails]
  );

  const isUserSelected = (email: string) =>
    Array.from(selected).some((a) => isSameAgentEmail(a, email));

  const toggleUser = (email: string) => {
    setSelected((prev) => {
      const prevList = Array.from(prev);
      const existing = prevList.find((a) => isSameAgentEmail(a, email));
      if (existing !== undefined) {
        return new Set(prevList.filter((a) => !isSameAgentEmail(a, email)));
      }
      // Cap on what Save will actually write (derived from prev, not the
      // memoized effectiveSelected which can be a stale closure across rapid
      // toggles) so the limit can never be crossed by a stale count.
      if (filterValidAgents(prevList, validAgentEmails).length >= 10) return prev; // Max 10 agents
      // Restore the ORIGINAL stored spelling when one existed — Twilio client
      // identities are case-sensitive, so a retick must not rewrite it. Pick
      // the LAST case-variant to match filterValidAgents (last wins).
      const original = [...assignedAgents].reverse().find((a) => isSameAgentEmail(a, email));
      return new Set([...prevList, original ?? email]);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-background border border-border rounded-xl shadow-xl w-80 max-h-[400px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <p className="text-sm font-medium text-foreground">Call Agents</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Who should receive calls to {callerId}?
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* User list */}
        <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
          {userList.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            userList.map((u) => {
              const isSelected = isUserSelected(u.email);
              return (
                <button
                  key={u.email}
                  onClick={() => toggleUser(u.email)}
                  className={cn(
                    'w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors',
                    isSelected
                      ? 'bg-primary/10 text-foreground'
                      : 'hover:bg-muted/50 text-muted-foreground'
                  )}
                >
                  {/* Avatar */}
                  {u.picture ? (
                    <img
                      src={u.picture}
                      alt=""
                      className="h-6 w-6 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground shrink-0">
                      {(u.name || u.email)[0]?.toUpperCase()}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">
                      {u.name || u.email}
                      {u.email === currentUserEmail && (
                        <span className="text-muted-foreground font-normal"> (you)</span>
                      )}
                    </p>
                    {u.name && (
                      <p className="text-[10px] text-muted-foreground truncate">{u.email}</p>
                    )}
                  </div>

                  {/* Checkmark */}
                  <div
                    className={cn(
                      'h-4 w-4 rounded border shrink-0 flex items-center justify-center transition-colors',
                      isSelected
                        ? 'bg-primary border-primary text-primary-foreground'
                        : 'border-border'
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <div>
            <p className="text-[10px] text-muted-foreground">
              {effectiveSelected.size} / 10 agents selected
            </p>
            {effectiveSelected.size === 0 && (
              <p className="text-[10px] text-destructive mt-0.5">Select at least 1 agent</p>
            )}
          </div>
          <button
            onClick={() => onSave(Array.from(effectiveSelected))}
            disabled={effectiveSelected.size === 0}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
